// 文件上传API模块 - 真实后端接口
import ActionAPI from './comfyui-action/index.js'

/**
 * 上传文件到真实后端
 * @param {Array|File} files 文件或文件数组
 * @param {Object} options 上传选项
 * @returns {Promise} 上传结果
 */
export const uploadFiles = async (files, options = {}) => {
  try {
    // 确保files是数组
    const fileList = Array.isArray(files) ? files : [files]
    
    const uploadPromises = fileList.map(file => uploadSingleFile(file, options))
    const results = await Promise.all(uploadPromises)
    
    return {
      success: true,
      data: results
    }
  } catch (error) {
    console.error('文件上传失败:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

/**
 * 上传单个文件到真实后端
 * @param {File} file 文件对象
 * @param {Object} options 上传选项
 * @returns {Promise} 上传结果
 */
const uploadSingleFile = async (file, options = {}) => {
  try {
    // 获取文件名
    const fileName = file.name || `file_${Date.now()}.${getFileExtension(file)}`
    
    // 使用真实的API上传
    const result = await ActionAPI.uploadFile(file, fileName)
    
    if (result.success) {
      return {
        success: true,
        url: result.data.file_url,
        filename: result.data.file_name,
        size: result.data.file_size,
        type: result.data.file_type
      }
    } else {
      throw new Error(result.error || '上传失败')
    }
  } catch (error) {
    console.error('文件上传失败:', error)
    throw error
  }
}

/**
 * 获取文件扩展名
 * @param {File} file 文件对象
 * @returns {string} 文件扩展名
 */
const getFileExtension = (file) => {
  if (file.name) {
    const parts = file.name.split('.')
    return parts.length > 1 ? parts[parts.length - 1] : 'bin'
  }
  
  if (file.type) {
    const mimeMap = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'video/mp4': 'mp4',
      'video/mov': 'mov',
      'video/avi': 'avi'
    }
    return mimeMap[file.type] || 'bin'
  }
  
  return 'bin'
}

/**
 * 选择并上传图片（H5环境）
 * @param {Object} options 选择选项
 * @returns {Promise} 上传结果
 */
export const chooseAndUploadImage = (options = {}) => {
  return new Promise((resolve, reject) => {
    // 创建文件输入元素
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = (options.count || 1) > 1
    
    input.onchange = async (e) => {
      try {
        const files = Array.from(e.target.files)
        if (files.length === 0) {
          reject(new Error('没有选择文件'))
          return
        }
        
        // 验证文件大小（默认20MB限制）
        const maxSize = options.maxSize || 20 * 1024 * 1024
        for (const file of files) {
          if (file.size > maxSize) {
            reject(new Error(`文件 ${file.name} 超过大小限制`))
            return
          }
        }
        
        const uploadResult = await uploadFiles(files, options)
        resolve(uploadResult)
      } catch (error) {
        reject(error)
      }
    }
    
    input.onerror = () => {
      reject(new Error('选择图片失败'))
    }
    
    // 触发文件选择
    input.click()
  })
}

/**
 * 选择并上传视频（H5环境）
 * @param {Object} options 选择选项
 * @returns {Promise} 上传结果
 */
export const chooseAndUploadVideo = (options = {}) => {
  return new Promise((resolve, reject) => {
    // 创建文件输入元素
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'video/*'
    
    input.onchange = async (e) => {
      try {
        const files = Array.from(e.target.files)
        if (files.length === 0) {
          reject(new Error('没有选择文件'))
          return
        }
        
        // 验证文件大小（默认50MB限制）
        const maxSize = options.maxSize || 50 * 1024 * 1024
        const file = files[0]
        if (file.size > maxSize) {
          reject(new Error(`视频文件超过大小限制`))
          return
        }
        
        const uploadResult = await uploadFiles([file], options)
        resolve(uploadResult)
      } catch (error) {
        reject(error)
      }
    }
    
    input.onerror = () => {
      reject(new Error('选择视频失败'))
    }
    
    // 触发文件选择
    input.click()
  })
}

/**
 * 直接上传文件（从File对象）
 * @param {File} file 文件对象
 * @param {Object} options 上传选项
 * @returns {Promise} 上传结果
 */
export const uploadFile = async (file, options = {}) => {
  try {
    return await uploadSingleFile(file, options)
  } catch (error) {
    return {
      success: false,
      error: error.message
    }
  }
}

/**
 * 删除文件（如果后端支持）
 * @param {string} fileUrl 文件URL
 * @returns {Promise} 删除结果
 */
export const deleteFile = async (fileUrl) => {
  try {
    // 注意：当前接口文档中没有删除文件的接口
    // 这里返回成功状态，实际可能需要根据后端实现
    console.warn('删除文件功能需要后端支持相应接口')
    
    return {
      success: true,
      message: '文件标记删除成功'
    }
  } catch (error) {
    return {
      success: false,
      error: error.message
    }
  }
}

export default {
  uploadFiles,
  uploadFile,
  chooseAndUploadImage,
  chooseAndUploadVideo,
  deleteFile
}
